import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "./maybe";
import { displayFileName } from "./fileNames";
import { entityEffectiveColor } from "./entityColors";
import { sortArcMoments } from "./entityArc";
import type { EntityArcLink, EntityFrontmatter } from "./entityFrontmatter";
import type { DatabaseEntrySummary } from "./tauri/database";
import { trimmedMaybe } from "./trimmedString";
import { chapterPrimaryPath, scenePath, type ZwChapter } from "./zwConfig";

/** Arc moment resolved on a manuscript scene. Rendered as a storyboard chip. */
export type StoryboardArcChip = {
  id: string;
  label: string;
  kind: "arc";
  /** `Database/` entry basename to open when the chip is clicked. */
  entryFile: string;
  momentId: string;
  /** Entry accent color from frontmatter. Tints the chip on the storyboard. */
  color: Maybe<string>;
};

/** Drop target in the storyboard sidebar for unlinking a moment from its scene. */
export const ARC_MOMENT_TRAY_KEY = "arc-moment-tray";

export function databaseEntryLabel(entry: DatabaseEntrySummary): string {
  return displayFileName(entry.name);
}

function arcMomentLinkedScene(link: Maybe<EntityArcLink>): Maybe<string> {
  return matchMaybe(link, {
    nothing: () => nothing,
    just: (l) => trimmedMaybe(l.file),
  });
}

/** Index arc moments by manuscript scene path (`Chapters/<folder>/<scene>.md`). */
export function buildStoryboardArcIndex(
  entries: DatabaseEntrySummary[],
): Map<string, StoryboardArcChip[]> {
  const index = new Map<string, StoryboardArcChip[]>();

  for (const entry of entries) {
    if (entry.isMap) continue;
    const entityLabel = databaseEntryLabel(entry);
    for (const arcMoment of sortArcMoments(entry.fields.arc)) {
      const linkedSceneM = arcMomentLinkedScene(arcMoment.link);
      if (isNothing(linkedSceneM)) continue;
      const linkedScene = linkedSceneM.value;
      if (entry.fields.excludeFromScene.includes(linkedScene)) continue;
      const chip: StoryboardArcChip = {
        id: `${entry.name}:${arcMoment.id}`,
        label: `${entityLabel} · ${arcMoment.moment}`,
        kind: "arc",
        entryFile: entry.name,
        momentId: arcMoment.id,
        color: entityEffectiveColor(entry.fields.color, entry.fields.type),
      };
      const existing = index.get(linkedScene) ?? [];
      existing.push(chip);
      index.set(linkedScene, existing);
    }
  }

  return index;
}

export function storyboardArcChipsForScene(
  scenePath: string,
  index: Map<string, StoryboardArcChip[]>,
): StoryboardArcChip[] {
  return index.get(scenePath) ?? [];
}

/** Arc moment with no manuscript scene link. Shown in the storyboard sidebar. */
export type StoryboardUnmappedMoment = {
  id: string;
  entryFile: string;
  momentId: string;
  entityLabel: string;
  moment: string;
  at: Maybe<string>;
  color: Maybe<string>;
};

export function listUnmappedArcMoments(
  entries: DatabaseEntrySummary[],
): StoryboardUnmappedMoment[] {
  const moments: StoryboardUnmappedMoment[] = [];
  for (const entry of entries) {
    if (entry.isMap) continue;
    const entityLabel = databaseEntryLabel(entry);
    for (const arcMoment of sortArcMoments(entry.fields.arc)) {
      if (isJust(arcMomentLinkedScene(arcMoment.link))) continue;
      moments.push({
        id: `${entry.name}:${arcMoment.id}`,
        entryFile: entry.name,
        momentId: arcMoment.id,
        entityLabel,
        moment: arcMoment.moment,
        at: arcMoment.at,
        color: entityEffectiveColor(entry.fields.color, entry.fields.type),
      });
    }
  }
  return moments;
}

/** Resolve the manuscript path when an unmapped moment is dropped on a board card. */
export function scenePathForArcMomentDrop(
  hit: {
    // Acts carry no prose, so they're never a valid landing spot for a moment.
    // `end-of-manuscript` is a chapter/act reorder zone only.
    kind:
      | "act"
      | "chapter"
      | "scene"
      | "chapter-tail"
      | "unplaced-tray"
      | "unplaced-item"
      | "arc-moment-tray"
      | "end-of-manuscript";
    folder: Maybe<string>;
    sceneFile: Maybe<string>;
  },
  chapters: ZwChapter[],
): Maybe<string> {
  if (
    hit.kind === "scene" &&
    isJust(hit.folder) &&
    hit.folder.value.length > 0 &&
    isJust(hit.sceneFile) &&
    hit.sceneFile.value.length > 0
  ) {
    return just(scenePath(hit.folder.value, hit.sceneFile.value));
  }
  if (
    hit.kind === "chapter" &&
    isJust(hit.folder) &&
    hit.folder.value.length > 0
  ) {
    const folder = hit.folder.value;
    const chapter = chapters.find((c) => c.folder === folder);
    const chapterM = typeof chapter === "undefined" ? nothing : just(chapter);
    if (isNothing(chapterM) || chapterM.value.scenes.length !== 1) {
      return nothing;
    }
    return just(chapterPrimaryPath(chapterM.value));
  }
  return nothing;
}

export function linkArcMomentToScene(
  fields: EntityFrontmatter,
  momentId: string,
  sceneFilePath: string,
): EntityFrontmatter {
  return {
    ...fields,
    excludeFromScene: fields.excludeFromScene.filter(
      (scene) => scene !== sceneFilePath,
    ),
    arc: fields.arc.map((arcMoment) =>
      arcMoment.id === momentId
        ? { ...arcMoment, link: just({ file: sceneFilePath }) }
        : arcMoment,
    ),
  };
}

export function unlinkArcMomentFromScene(
  fields: EntityFrontmatter,
  momentId: string,
): EntityFrontmatter {
  return {
    ...fields,
    arc: fields.arc.map((arcMoment) =>
      arcMoment.id === momentId ? { ...arcMoment, link: nothing } : arcMoment,
    ),
  };
}
